import { ReactNode, createContext, useContext } from "react";

import { Ds } from "@accurx/design";
import {
    PatientDemographics,
    useBrowserEnvironment,
    useMedicalRecordConnection,
    useNativeSubscription,
} from "@accurx/native";

import { DisconnectedEmrErrorPage } from "../../components/DisconnectedEmrErrorPage/DisconnectedEmrErrorPage";
import { StyledSpinnerWrapper } from "./CurrentPatientProvider.styles";

export const NativeFeedContext = createContext<{
    medicalRecordPatient: PatientDemographics | null;
    hasError: boolean;
} | null>(null);

export const NativePatientFeedProvider = ({
    children,
}: {
    children: ReactNode;
}) => {
    const medicalRecordConnection = useMedicalRecordConnection();
    const browserEnv = useBrowserEnvironment();

    // Gets updated in the background, only works when connected to a medical record
    const medicalRecordPatientFeed = useNativeSubscription(
        "SubscribeCurrentPatient",
    );

    if (
        browserEnv === "WebView" &&
        medicalRecordPatientFeed.status === "loading"
    ) {
        return (
            <StyledSpinnerWrapper>
                <Ds.Spinner />
            </StyledSpinnerWrapper>
        );
    }

    if (
        browserEnv === "WebView" &&
        !medicalRecordPatientFeed.data &&
        medicalRecordConnection.status === "Disconnected"
    ) {
        return <DisconnectedEmrErrorPage />;
    }

    return (
        <NativeFeedContext.Provider
            value={{
                medicalRecordPatient: medicalRecordPatientFeed.data ?? null,
                hasError: medicalRecordPatientFeed.status === "error",
            }}
        >
            {children}
        </NativeFeedContext.Provider>
    );
};

export const useMedicalRecordFeedPatient = () => {
    const context = useContext(NativeFeedContext);

    if (context === null) {
        throw new Error(
            "useMedicalRecordFeedPatient must be used within an NativePatientFeedProvider",
        );
    }
    return context;
};
